import classnames from 'classnames';
import throttle from 'lodash/throttle';
import * as React from 'react';

import styles from './soundMeter.module.scss';

export type Sound = {
  input: number;
  output: number;
  isRecording: boolean;
};

type SoundMeterProps = {
  /** Extra classnames */
  className?: string;
  sounds: Sound[];
};

const recordingColor = 'red';
const notRecordingColor = '#9b9b9b';
const defaultWidth = '300';
const defaultHeight = '16';

export class SoundMeter extends React.PureComponent<SoundMeterProps> {
  soundMeterRef: React.RefObject<HTMLDivElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;

  constructor(props: SoundMeterProps) {
    super(props);
    this.soundMeterRef = React.createRef();
    this.canvasRef = React.createRef();
    this.setupCanvas = this.setupCanvas.bind(this);
    this.setupCanvas = throttle(this.setupCanvas, 200);
  }

  componentDidMount() {
    this.setupCanvas();
    window.addEventListener('resize', this.setupCanvas);
  }

  componentDidUpdate(prevProps: SoundMeterProps) {
    const { sounds } = this.props;

    if (sounds !== prevProps.sounds) {
      this.updateCanvas();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setupCanvas);
  }

  setupCanvas() {
    if (!this.soundMeterRef.current) {
      return;
    }
    const canvas = this.canvasRef.current;
    if (!canvas) {
      return;
    }

    const { width, height } =
      this.soundMeterRef.current.getBoundingClientRect();

    canvas.width = width;
    canvas.height = height;
    this.updateCanvas();
  }

  updateCanvas() {
    const { sounds } = this.props;

    const canvas = this.canvasRef.current;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return;
    }

    ctx.fillStyle = notRecordingColor;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    let prevIsRecording: boolean;

    sounds.slice(-canvas.width / 4).forEach((sound, index) => {
      if (sound.isRecording !== prevIsRecording) {
        ctx.fillStyle = sound.isRecording ? recordingColor : notRecordingColor;
      }
      prevIsRecording = sound.isRecording;
      const height = (sound.input + sound.output) * canvas.height;
      const y = (canvas.height - height) / 2;
      ctx.fillRect(index * 4, y, 2, height);
    });
  }

  render() {
    const { className, sounds, ...otherProps } = this.props;

    const rootClass = classnames(
      {
        [styles.soundMeter]: true,
      },
      className,
    );

    return (
      <div ref={this.soundMeterRef} {...otherProps} className={rootClass}>
        <canvas
          ref={this.canvasRef}
          width={defaultWidth}
          height={defaultHeight}
        />
      </div>
    );
  }
}
